import React, { useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import eye from '@iconify/icons-solar/eye-linear';
import eyeClosed from '@iconify/icons-solar/eye-closed-linear';
import arrowRight from '@iconify/icons-solar/arrow-right-linear';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';
import './Login.css';

interface LoginProps {
  onLogin: (user: any) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const { addToast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const submitting = useRef(false);

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setLoading(true);
    setError('');
    try {
      const data = await api.auth.login(email.trim(), password);
      const user = { ...data.user, token: data.token };
      onLogin(user);
      addToast('success', 'Welcome Back', `Logged in as ${user.name}`);
    } catch (err: any) {
      setError(err.message || 'Unable to sign in. Check your email and password, then try again.');
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  return (
    <main className="staff-login">
      <section className="staff-login-panel" aria-labelledby="staff-login-title">
        <img className="staff-login-logo" src="/logo.png" alt="Valley of Peace Park of Life" width={160} />
        <header className="staff-login-heading">
          <h1 id="staff-login-title">Staff sign in</h1>
          <p>Access your VOP CRM workspace.</p>
        </header>
        <form onSubmit={handleLogin} className="staff-login-form" aria-busy={loading} aria-describedby={error ? "staff-login-error" : undefined}>
          <div className="staff-login-field">
            <label htmlFor="staff-email">Email address</label>
            <input id="staff-email" name="email" type="email" autoComplete="username" inputMode="email"
              autoCapitalize="none" spellCheck={false} placeholder="you@company.com" required readOnly={loading}
              value={email} onChange={(event) => setEmail(event.target.value)} />
          </div>
          <div className="staff-login-field">
            <label htmlFor="staff-password">Password</label>
            <div className="staff-login-password">
              <input id="staff-password" name="password" type={showPassword ? 'text' : 'password'}
                autoComplete="current-password" placeholder="Enter your password" required readOnly={loading}
                value={password} onChange={(event) => setPassword(event.target.value)} />
              <button type="button" className="staff-login-visibility" aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-controls="staff-password" aria-pressed={showPassword} onClick={() => setShowPassword(!showPassword)}>
                <Icon icon={showPassword ? eyeClosed : eye} width={20} aria-hidden="true" />
              </button>
            </div>
          </div>
          {error && <p id="staff-login-error" className="staff-login-error" role="alert"><strong>Sign-in failed.</strong> {error}</p>}
          <button className="staff-login-submit" type="submit" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
        <p className="staff-login-help">Need an account or help signing in?<br />Contact your VOP CRM administrator.</p>
        <section className="staff-login-client" aria-labelledby="client-access-title">
          <h2 id="client-access-title">Client access</h2>
          <p>Use the client portal to access your account.</p>
          <a className="staff-login-portal" href="/portal/login">Open client portal<Icon icon={arrowRight} width={18} aria-hidden="true" /></a>
        </section>
      </section>
    </main>
  );
};

export default Login;
